import React, { useCallback, useMemo, useState } from 'react';
import { useMedia } from 'react-media';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTabItem,
  ModalTabs,
  Text,
  View,
} from '../ui/index';
import type { ShippingPlanProps } from './ShippingPlan';
import ShippingPlan from './ShippingPlan';
import type {
  RelatedOrgsQuery,
  ScenarioTimeline_ShipFragment,
  VoyageCharterUpdates_UpdateFragment,
} from '../../graphql';
import type { ModalProps } from '../ui/Modal/Modal';
import type { ChangedEntries, PortCallData } from '../core/Core';
import type { Vessel } from '../core/Vessel';
import type { SingleEventType, Event } from '../core/TimeData';
import {
  isPortCallArrivalEvent,
  isLoadEventTimeData,
  isPortCallEventTimeData,
} from '../core/TimeData';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type {
  CargoAllocation,
  CoaEntry,
  SpotEntry,
} from '@seaber/common/core/entries';
import { hToMs } from '../../lib/time';
import { assertDefined, isDefined } from '../../lib/util';
import SpotContract from './SpotContract';
import ShipmentCoa from './ShipmentCoa';
import Invoices from './Invoices';
import PortCallsDocuments from './PortCallsDocuments';
import Costs from './VoyageCosts';
import Changelog from './Changelog';
import type { EventTimeline, TimelineFocus } from '../core/Timelines';
import { getSubTimeline } from '../core/Timelines';
import type { ScenarioTimelineProps } from '../timeline/ScenarioTimeline';
import type { ShipmentModalNavTarget, ShipmentModalTab } from './shipment';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import type {
  CompletedData,
  ScenarioOrder,
  TcTimeDataByVessel,
} from '../../lib/planningData';
import Tanks from './Tanks';
import { toast } from 'react-toastify';
import { ShipmentMap } from './ShipmentMap';
import VesselInfo from './VesselInfo';
import type { ClientId } from '../core/ClientId';
import { hasFeature, hasUserRight, useCurrentUser } from '../../state/zustand';
import type { PnlData } from '../hooks/usePnlData';
import CargoSummary from './CargoSummary';
import compact from 'lodash/compact';

export type ShipmentModalProps = {
  spotAssignments?: readonly SpotEntry[];
  coaAssignments?: readonly CoaEntry[];
  vessels?: ReadonlyArray<Vessel>;
  vessel?: Vessel;
  vesselIndex?: number;
  orders?: ScenarioOrder[];
  tcTimeDataByVessel?: TcTimeDataByVessel | undefined;
  eventTimelines?: EventTimeline[];
  timelineFocus?: TimelineFocus;
  onUnassignOrder?: ShippingPlanProps['onUnassign'];
  handleUpdateEntry?: (
    eventTimeline: EventTimeline,
    event: Event,
    change: Partial<Event>,
    modalKey?: string,
  ) => void;
  handleModalSwap?: ScenarioTimelineProps['onSwapScheduleEntry'];
  onSwapPortCall?: ScenarioTimelineProps['onSwapPortCall'];
  handleDelete?: (
    vesselId: number,
    indexToDelete: number,
    modalKey?: string,
  ) => void;
  handleCreateEvent?: (
    type: SingleEventType,
    vesselId: number,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    duration: number,
    index: number,
    start: number,
    modalKey?: string,
  ) => void;
  onOptimizeVoyageSpeed?: ShippingPlanProps['onOptimizeVoyageSpeed'];
  onClose: () => void;
  onVesselChange?: (
    contract:
      | { type: 'spot'; shipownerOrgId: number | null }
      | { type: 'coa'; coaId: number },
    orderIds: number[],
    vesselId: number | null,
    startTime: number,
  ) => void;
  onSpotContractVesselChange?: (
    voyageCharterId: number,
    orderIds: number[],
    shipownerOrgId: number | null,
    vesselId: number | null,
    startTime: number,
  ) => void;
  modalKey: string;
  scenario?: ScenarioSchedule;
  navTarget?: ShipmentModalNavTarget;
  saveAllocations?: (allocations: CargoAllocation[]) => void;
  scheduleContext?: ScheduleDataContext;
  createAndUpdateAssignments?: (changed: ChangedEntries) => void;
  pnlData?: PnlData;
  completedData?: CompletedData;
  completedOrderIds?: Set<number>;
  relatedOrgsData?: RelatedOrgsQuery | undefined;
} & Partial<ModalProps>;

const getPortCallArrivalTime = (portCall: PortCallData): number | null => {
  return (
    portCall.tds.find(
      (td) => isPortCallEventTimeData(td) && isPortCallArrivalEvent(td.event),
    )?.start ?? null
  );
};

const ShipmentModal = (props: ShipmentModalProps): JSX.Element | null => {
  const { timelineFocus, eventTimelines, completedOrderIds, completedData } =
    props;
  const { timelineId, orderIds } = timelineFocus ?? {};
  const hasCompletedOrders = useMemo(
    () =>
      (completedOrderIds &&
        orderIds?.some((id) => completedOrderIds.has(id))) ||
      false,
    [completedOrderIds, orderIds],
  );

  const eventTimeline = useMemo(() => {
    const timelines =
      (hasCompletedOrders && completedData?.timelines) || eventTimelines;
    return timelines?.find((t) => t.id === timelineId);
  }, [
    completedData?.timelines,
    hasCompletedOrders,
    eventTimelines,
    timelineId,
  ]);

  const onClose = props.onClose;
  React.useEffect(() => {
    if (!eventTimeline) {
      onClose();
    }
  }, [eventTimeline, onClose]);

  if (!eventTimeline) {
    // Event timeline can become undefined if shipment was moved while modal was open
    return null;
  }

  return (
    <ShipmentModalEventTimeline
      {...props}
      eventTimeline={eventTimeline}
      hasCompletedOrders={hasCompletedOrders}
    />
  );
};

const ShipmentModalEventTimeline = ({
  vessels,
  onClose,
  modalKey,
  handleUpdateEntry,
  handleModalSwap,
  onSwapPortCall,
  handleCreateEvent,
  handleDelete,
  onVesselChange,
  spotAssignments,
  coaAssignments,
  eventTimelines,
  timelineFocus,
  onUnassignOrder,
  scenario,
  onSpotContractVesselChange,
  tcTimeDataByVessel,
  navTarget,
  saveAllocations,
  orders = [],
  scheduleContext,
  eventTimeline,
  createAndUpdateAssignments,
  pnlData,
  hasCompletedOrders,
  relatedOrgsData,
  onOptimizeVoyageSpeed,
  ...restProps
}: ShipmentModalProps & {
  eventTimeline: EventTimeline;
  hasCompletedOrders: boolean;
}): JSX.Element | null => {
  const currentUser = useCurrentUser();
  assertDefined(currentUser, 'current user');
  assertDefined(scenario, 'scenario not provided to shipment modal');

  const isCharterer = currentUser.organization?.charterer ?? false;
  const isRootOrgUser =
    typeof currentUser.organization?.parentOrganizationId !== 'number';

  const focusedSubTimeline = useMemo(
    () => getSubTimeline(eventTimeline, timelineFocus),
    [eventTimeline, timelineFocus],
  );

  const allLoadEvents = focusedSubTimeline.voyageGroups
    .flatMap((vg) => vg.events)
    .filter(isLoadEventTimeData);

  const portCalls = useMemo(() => {
    return focusedSubTimeline.voyageGroups.flatMap((vg) => vg.portCalls);
  }, [focusedSubTimeline.voyageGroups]);

  const portCallIds = useMemo(() => portCalls.map((pc) => pc.id), [portCalls]);
  const portCallClientIds: ClientId[] = useMemo(
    () => [...new Set(portCallIds)].filter(isDefined),
    [portCallIds],
  );
  const portCallDbIds = useMemo(() => {
    const portCallClientIdsSet = new Set(portCallIds);
    return scenario.portCalls
      .filter((pc) => portCallClientIdsSet.has(pc.clientId))
      .map((pc) => pc.dbId)
      .filter(isDefined);
  }, [portCallIds, scenario]);

  const focusedShipments = useMemo<Set<ClientId>>(() => {
    if (!timelineFocus) return new Set();

    const ordersInTimelineFocus = orders.filter(
      (o) => timelineFocus.orderIds?.includes(o.id) ?? false,
    );

    const focusedOrderShipmentIds = ordersInTimelineFocus
      .map((o) => o.shipmentId)
      .filter(isDefined);

    return new Set([
      ...focusedOrderShipmentIds,
      ...compact(timelineFocus.shipmentIds),
    ]);
  }, [orders, timelineFocus]);

  const focusedOrders = useMemo(
    () =>
      timelineFocus
        ? orders.filter((o) => {
            const isInFocusedOrders =
              timelineFocus.orderIds?.includes(o.id) ?? false;
            const isInFocusedShipments =
              isDefined(o.shipmentId) && focusedShipments.has(o.shipmentId);

            return isInFocusedOrders || isInFocusedShipments;
          })
        : [],
    [orders, timelineFocus, focusedShipments],
  );

  const focusedOrderIds = useMemo(
    () => new Set(focusedOrders.map((o) => o.id)),
    [focusedOrders],
  );

  const selectedCoaAssignments =
    coaAssignments?.filter((coaAssignment) =>
      focusedOrderIds.has(coaAssignment.orderId),
    ) ?? [];

  const coaId = eventTimeline.type === 'coa' ? eventTimeline.coaId : null;

  const selectedSpotAssignments =
    spotAssignments?.filter(
      (assignment) =>
        assignment.orderId && focusedOrderIds.has(assignment.orderId),
    ) ?? [];

  const voyageCharterId = useMemo<number | null>(() => {
    let scenarioVoyageCharterId: number | null | undefined = undefined;
    let persistedVoyageCharterId: number | null = null;

    // Currently this logic is a bit messy, we either choose _any_ available
    // voyage charter override or the _first_ available persisted voyage charter.
    // In reality there might be orders from multiple voyage charters in the view
    // and there might more edge cases so this logic should be revisited.
    focusedOrders.forEach((o) => {
      const scenarioVc = scenario.voyageCharterByOrderId.get(o.id);
      const persistedVc =
        o.outgoingVoyageCharterRelation?.voyageCharterId ?? null;

      if (scenarioVc !== undefined) {
        scenarioVoyageCharterId =
          typeof scenarioVc === 'string' ? null : scenarioVc;
        return;
      }

      if (persistedVoyageCharterId === null && persistedVc !== null) {
        persistedVoyageCharterId = persistedVc;
      }
    });

    return scenarioVoyageCharterId !== undefined
      ? scenarioVoyageCharterId
      : persistedVoyageCharterId;
  }, [scenario.voyageCharterByOrderId, focusedOrders]);

  let vessel: ScenarioTimeline_ShipFragment | undefined = undefined;

  if (eventTimeline.type === 'tc') {
    vessel = eventTimeline.vessel;
  } else if (vessels && selectedCoaAssignments[0]?.vesselId) {
    vessel = vessels.find(
      (vessel) => vessel.id === selectedCoaAssignments[0].vesselId,
    );
  } else if (vessels && selectedSpotAssignments[0]?.vesselId) {
    vessel = vessels.find(
      (vessel) => vessel.id === selectedSpotAssignments[0].vesselId,
    );
  }

  const vesselId = vessel && vessel.id;

  const isVesselChangeEnabled =
    onVesselChange !== undefined &&
    (eventTimeline.type === 'coa' || eventTimeline.type === 'spot') &&
    (focusedShipments.size === 1 ||
      (focusedOrders.length === 1 && focusedOrders[0].shipmentId == null)); // Some orders might not have a shipmentId

  const handleVesselChange = useCallback(
    (vesselId: number) => {
      if (!onVesselChange || eventTimeline.type === 'tc') return;

      const startTime = getPortCallArrivalTime(portCalls[0]);

      if (!startTime) {
        console.error('Could not find start time for shipment');
        toast.error('Could not find start time for shipment');
        return;
      }

      onVesselChange(
        eventTimeline.type === 'spot'
          ? { type: 'spot', shipownerOrgId: eventTimeline.ownerOrganizationId }
          : { type: 'coa', coaId: eventTimeline.coaId },
        Array.from(focusedOrderIds),
        vesselId,
        startTime,
      );

      onClose?.();
    },
    [eventTimeline, focusedOrderIds, onVesselChange, onClose, portCalls],
  );

  const handleSpotContractVesselChange = useCallback(
    (
      spotContractId: number,
      shipownerOrgId: number | null,
      vesselId: number | null,
    ) => {
      if (!onSpotContractVesselChange) return;

      const startTime = getPortCallArrivalTime(portCalls[0]);

      if (!startTime) {
        console.error('Could not find start time for shipment');
        toast.error('Could not find start time for shipment');
        return;
      }

      onSpotContractVesselChange(
        spotContractId,
        Array.from(focusedOrderIds),
        shipownerOrgId,
        vesselId,
        startTime,
      );

      onClose?.();
    },
    [focusedOrderIds, onSpotContractVesselChange, portCalls, onClose],
  );

  const handleVoyageCharterUpdateApprove = useCallback(
    (update: VoyageCharterUpdates_UpdateFragment) => {
      if (update.vesselId && update.vesselId !== vesselId) {
        handleVesselChange(update.vesselId);
      }
    },
    [handleVesselChange, vesselId],
  );

  const shipInput = vesselId
    ? tcTimeDataByVessel?.get(vesselId)?.inputs
    : undefined;

  const orderOrgs = focusedOrders
    .map((o) => o.chartererOrganizationId)
    .filter(isDefined);
  const hasFinancialRights = orderOrgs.every((orgId) =>
    hasUserRight(orgId, 'accessFinancialData'),
  );

  // Feature flags
  const isCargoCompartmentsActive = hasFeature('cargo_compartments');

  const draft = currentUser.rootOrganization?.draft;

  const tabs: {
    [key: string]: {
      label: string;
      short: string;
      component: () => React.ReactNode;
    };
  } = {
    shippingPlan: {
      label: 'Shipping plan',
      short: 'Shipp..',
      component: () => {
        return (
          <ShippingPlan
            isEditable={
              !hasCompletedOrders &&
              (!!handleUpdateEntry ||
                !!handleModalSwap ||
                !!handleDelete ||
                !!handleCreateEvent)
            }
            onEventChange={(event, change) => {
              handleUpdateEntry?.(focusedSubTimeline, event, change, modalKey);
            }}
            onSwap={handleModalSwap}
            onSwapPortCall={onSwapPortCall}
            onOptimizeVoyageSpeed={onOptimizeVoyageSpeed}
            onDelete={(index: number) => {
              if (vesselId && handleDelete) {
                handleDelete(vesselId, index, modalKey);
              }
            }}
            onCreateSingleEvent={
              vesselId
                ? (
                    type: SingleEventType,
                    locationId: number,
                    locationName: string,
                    locationTimeZone: string | null,
                    index: number,
                    start: number,
                    duration?: number,
                  ) => {
                    if (handleCreateEvent) {
                      handleCreateEvent(
                        type,
                        vesselId,
                        locationId,
                        locationName,
                        locationTimeZone,
                        duration || hToMs(1),
                        index,
                        start,
                        modalKey,
                      );
                    }
                  }
                : () => null
            }
            eventTimeline={focusedSubTimeline}
            shipInput={shipInput}
            onUnassign={onUnassignOrder}
            timelineFocus={timelineFocus}
            vessel={vessel}
            focusedOrders={focusedOrders ?? []}
            currentUser={currentUser}
            isVesselChangeEnabled={isVesselChangeEnabled}
            onVesselChange={handleVesselChange}
            coaId={coaId}
            voyageCharterId={voyageCharterId}
            scheduleContext={scheduleContext}
            onVoyageCharterUpdateApprove={handleVoyageCharterUpdateApprove}
            scenario={scenario}
          />
        );
      },
    },
    map: {
      label: 'Map',
      short: 'Map',
      component: () => (
        <ShipmentMap timeline={focusedSubTimeline} draft={draft} />
      ),
    },
    ...(eventTimeline.type === 'tc' &&
    saveAllocations &&
    isCargoCompartmentsActive
      ? {
          tanks: {
            label: 'Cargo compartments',
            short: 'Tanks & Holds',
            component: () => (
              <Tanks
                vessel={vessel}
                scenario={scenario}
                portCalls={portCalls}
                eventTimeline={eventTimeline}
                saveAllocations={saveAllocations}
              />
            ),
          },
        }
      : {}),
    ...(eventTimeline.type === 'spot' &&
    (isCharterer || isRootOrgUser) &&
    selectedSpotAssignments.length > 0
      ? {
          spotContract: {
            label: 'Spot contract',
            short: 'Spot c..',
            component: () => (
              <SpotContract
                spotAssignments={selectedSpotAssignments}
                onClose={onClose}
                onVesselChange={handleSpotContractVesselChange}
                spotContractId={voyageCharterId}
              />
            ),
          },
        }
      : {}),
    ...((isCharterer || isRootOrgUser) && coaId
      ? {
          coa: {
            label: 'COA',
            short: 'COA',
            component: () => <ShipmentCoa coaId={coaId} />,
          },
        }
      : {}),
    documents: {
      label: 'Port calls / documents',
      short: 'Port call..',
      component: () => {
        const order = focusedOrders.at(0);
        const voyageName =
          order?.shipment?.name ?? `shipment-${order?.shipmentId}`;
        return (
          <PortCallsDocuments
            voyageName={voyageName}
            scenario={scenario}
            portCalls={portCalls}
            navTarget={navTarget}
            relatedOrgsData={relatedOrgsData}
          />
        );
      },
    },
    ...(hasFinancialRights &&
    (isCharterer || isRootOrgUser) &&
    voyageCharterId !== null
      ? {
          financials: {
            label: 'Invoices',
            short: 'Invoices',
            component: () => <Invoices voyageCharterId={voyageCharterId} />,
          },
        }
      : {}),

    ...(hasFinancialRights
      ? {
          costs: {
            label: 'Voyage P/L',
            short: 'Voyage P/L',
            component: () => (
              <Costs
                scenario={scenario}
                timeline={focusedSubTimeline}
                focusedOrderIds={focusedOrderIds}
                focusedShipments={focusedShipments}
                scheduleContext={scheduleContext}
                pnlData={pnlData}
                createAndUpdateAssignments={createAndUpdateAssignments}
                orders={orders}
              />
            ),
          },
        }
      : {}),

    vesselRemarks: {
      label: 'Vessel info',
      short: 'Vessel info',
      component: () => {
        if (!vessel) return null;
        return <VesselInfo vessel={vessel} />;
      },
    },

    changelog: {
      label: 'Changelog',
      short: 'Changelog',
      component: () => (
        <Changelog
          portCallClientIds={portCallClientIds}
          portCallDbIds={portCallDbIds}
          voyageCharterIds={voyageCharterId ? [voyageCharterId] : []}
        />
      ),
    },

    cargoSummary: {
      label: 'Cargo summary',
      short: 'Cargo summary',
      component: () => <CargoSummary allLoadEvents={allLoadEvents} />,
    },
  };

  const activeTabName = navTarget?.tabName || 'shippingPlan';
  const [activeTab, setActiveTab] = useState(activeTabName);

  const isSmallScreen = useMedia({ query: '(max-width: 992px)' });
  const shipmentName = focusedOrders.find((x) => x.shipment?.name)?.shipment
    ?.name;

  const voyageName = `${vessel?.name ? `${vessel.name}` : ``}${
    shipmentName ? ` - ${shipmentName}` : ``
  }`;

  return (
    <Modal isOpen onClose={onClose} {...restProps}>
      <View flexDirection="row" justifyContent="flex-start" alignItems="center">
        <Text color="neutral.white_50" mx={5} fontWeight="bold">
          {vessel
            ? voyageName
            : eventTimeline.type === 'spot'
              ? 'Spot voyage'
              : 'Coa voyage'}
        </Text>
        <ModalTabs>
          {Object.keys(tabs).map((tab: ShipmentModalTab) => {
            const tabComponent = tabs[tab];
            return !tabComponent ? null : (
              <ModalTabItem
                key={tab}
                isSmall={isSmallScreen}
                isActive={activeTab === tab}
                onPress={() => setActiveTab(tab)}
                label={tabComponent.label}
              >
                {isSmallScreen ? tabComponent.short : tabComponent.label}
              </ModalTabItem>
            );
          })}
        </ModalTabs>
      </View>
      <ModalBody p={0} overflowY="auto">
        {tabs[activeTab]?.component()}
      </ModalBody>
      {activeTab !== 'spotContract' && (
        <ModalFooter>
          <Button aria-label="close" onPress={onClose}>
            {'Close'}
          </Button>
          <div style={{ flexGrow: 1 }} />
        </ModalFooter>
      )}
    </Modal>
  );
};

export default ShipmentModal;
